import { z } from "zod";
import {
    draftDateTimeToIso,
    draftDateTimeToIsoEndOfMinute,
    isoToDraftDateTime,
} from "@/features/audit/utils/auditDateTimeFilter";
import type { AuditFiltersDraft } from "@/features/audit/components/AuditFiltersPanel";

export const auditScopeModeSchema = z.enum(["labels", "diaries"]);

export const auditLogSearchSchema = z.object({
    scope: auditScopeModeSchema.catch("labels"),
    labels: z.string().optional(),
    diaries: z.string().optional(),
    orgs: z.string().optional(),
    table: z.string().optional(),
    /** UTC lower bound for changed_at - not named "from" (TanStack Router reserved). */
    changedFrom: z.string().optional(),
    /** UTC upper bound for changed_at - not named "to" (TanStack Router reserved). */
    changedTo: z.string().optional(),
    asOf: z.string().optional(),
});

export type AuditLogSearch = z.infer<typeof auditLogSearchSchema>;

export function parseCsv(value?: string): string[] {
    if (!value) return [];
    return value
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
}

export function toCsv(values: string[]): string | undefined {
    return values.length > 0 ? values.join(",") : undefined;
}

export function defaultAuditLogFrom(): string {
    return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
}

/** Strip empty optional fields before writing to the URL. */
export function serializeAuditLogSearch(search: AuditLogSearch): AuditLogSearch {
    const next: AuditLogSearch = { scope: search.scope ?? "labels" };
    if (search.labels) next.labels = search.labels;
    if (search.diaries) next.diaries = search.diaries;
    if (search.orgs) next.orgs = search.orgs;
    if (search.table) next.table = search.table;
    if (search.changedFrom) next.changedFrom = search.changedFrom;
    if (search.changedTo) next.changedTo = search.changedTo;
    if (search.asOf) next.asOf = search.asOf;
    return next;
}

export function draftToAuditLogSearch(
    draft: AuditFiltersDraft,
    existing: AuditLogSearch,
    defaultFromIso: string
): AuditLogSearch {
    return serializeAuditLogSearch({
        scope: draft.scopeMode,
        labels: toCsv(draft.labels),
        diaries: toCsv(draft.diaryIds),
        orgs: toCsv(draft.organisationIds),
        table: toCsv(draft.tables),
        changedFrom: draft.from ? draftDateTimeToIso(draft.from) : defaultFromIso,
        changedTo: draft.to ? draftDateTimeToIsoEndOfMinute(draft.to) : undefined,
        asOf: existing.asOf,
    });
}

export function searchToAuditFiltersDraft(search: AuditLogSearch, defaultFromIso: string): AuditFiltersDraft {
    const defaultFromLocal = isoToDraftDateTime(defaultFromIso);

    return {
        scopeMode: search.scope,
        labels: parseCsv(search.labels),
        diaryIds: parseCsv(search.diaries),
        organisationIds: parseCsv(search.orgs),
        tables: parseCsv(search.table),
        from: search.changedFrom ? isoToDraftDateTime(search.changedFrom) : defaultFromLocal,
        to: search.changedTo ? isoToDraftDateTime(search.changedTo) : "",
    };
}
